import { useState, useMemo, useCallback, useEffect } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { useCachedData } from '@/hooks/useCachedData'
import type { Lead, EtapaPipeline, OrigenLead, PipelineSubestado } from '@/lib/types'
import PipelineBoard from '@/components/crm/PipelineBoard'
import LeadDetailView from '@/components/crm/LeadDetailView'
import CierreLeadModal, { mensajeDeCierre } from '@/components/crm/CierreLeadModal'
import { showToast } from '@/components/layout/Toast'
import EmptyState from '@/components/shared/EmptyState'
import {
  IconPlus, IconTarget, IconTrash, IconAlertCircle, IconX, IconCopy,
} from '@/components/icons/Icons'
import { PageHeader } from '@/components/ui'

const ORIGIN_FILTERS: { key: OrigenLead | 'todos'; label: string }[] = [
  { key: 'todos', label: 'Todos' },
  { key: 'meta_ads', label: 'Meta Ads' },
  { key: 'web', label: 'Web' },
  { key: 'referido', label: 'Referido' },
  { key: 'workana', label: 'Workana' },
  { key: 'chatbot', label: 'Chatbot' },
  { key: 'booking', label: 'Booking' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'radio_tv', label: 'Radio/TV' },
  { key: 'campana_email', label: 'Campaña Email' },
  { key: 'outbound', label: 'Outbound' },
  { key: 'otro', label: 'Otro' },
]

const COUNTRY_CODES = [
  { code: '+51', label: '🇵🇪 +51' },
  { code: '+1', label: '🇺🇸 +1' },
  { code: '+52', label: '🇲🇽 +52' },
  { code: '+57', label: '🇨🇴 +57' },
  { code: '+56', label: '🇨🇱 +56' },
  { code: '+54', label: '🇦🇷 +54' },
  { code: '+34', label: '🇪🇸 +34' },
  { code: '+593', label: '🇪🇨 +593' },
]

export default function Pipeline() {
  const api = useApi()
  /*
    La página se monta en el ERP y en el espacio del partner. Lo que cambia es
    poco, pero tiene que cambiar: fusionar leads es solo-admin en el backend
    (`POST /api/leads/merge` responde 403 a un partner), así que el botón
    sobraba y terminaba en un error que nadie veía.
  */
  const enPartner = useLocation().pathname.startsWith('/partner')
  const [errorAccion, setErrorAccion] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filtroOrigen, setFiltroOrigen] = useState<OrigenLead | 'todos'>('todos')
  const [showNewLead, setShowNewLead] = useState(false)
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  /* El lead que se está cerrando: el modal pregunta ganado o perdido. */
  const [cerrando, setCerrando] = useState<Lead | null>(null)
  const [cerrandoPaso, setCerrandoPaso] = useState<string | null | undefined>(undefined)
  /* Los pasos que configuró el dueño del tablero. Si la consulta falla, el
     tablero es el de siempre: seis columnas sin desplegar. */
  const [subestados, setSubestados] = useState<PipelineSubestado[]>([])
  /*
    `?lead=N` abre esa ficha directamente. El tablero guardaba la selección
    solo en memoria, así que no había forma de enlazar a una oportunidad: la
    bandeja de tareas del CRM manda acá al pulsar una tarea y se quedaba en el
    tablero, sin abrir nada.
  */
  const [params, setParams] = useSearchParams()
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [mergeSurvivorId, setMergeSurvivorId] = useState<number | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)


  const [form, setForm] = useState({
    contacto_nombre: '', contacto_apellido: '', contacto_email: '',
    codigo_telefono: '+51', contacto_telefono: '',
    empresa: '', es_agencia: false, origen: 'web' as OrigenLead,
  })
  const [saving, setSaving] = useState(false)

  // El tablero en cache: volver al Pipeline no vuelve a pedir los leads. Las
  // escrituras siguen siendo las de antes y avisan con `refrescar()` o retocan
  // el cache con `actualizar()`, que es lo que mantiene el drag & drop
  // instantaneo.
  const { data: leads = [], isLoading: loading, refrescar, actualizar } = useCachedData(
    ['leads'], () => api.get<Lead[]>('/api/leads'))
  useEffect(() => {
    const idUrl = Number(params.get('lead'))
    if (!idUrl || leads.length === 0) return
    // Una sola vez: si la ficha ya está abierta no se reabre, y el parámetro
    // se limpia para que cerrar la ficha no la vuelva a abrir.
    if (selectedLead?.id === idUrl) return
    const lead = leads.find(l => l.id === idUrl)
    if (lead) {
      setSelectedLead(lead)
      params.delete('lead')
      setParams(params, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, params])

  /* Los pasos del dueño del tablero. Una sola vez: cambian en Configuración,
     no mientras se arrastra una tarjeta. Si la consulta falla, el tablero es
     el de siempre: seis columnas sin desplegar. */
  useEffect(() => {
    let vivo = true
    api.get<PipelineSubestado[]>('/api/pipeline/subestados')
      .then(r => { if (vivo) setSubestados(Array.isArray(r) ? r : []) })
      .catch(() => { /* sin pasos, tablero de siempre */ })
    return () => { vivo = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Refresco sin spinner: trae la lista y deja la ficha abierta apuntando a la
  // fila nueva en vez de cerrarla.
  const silentRefresh = useCallback(async () => {
    const r = await refrescar()
    const data = r.data
    if (!data) return
    setSelectedLead(prev => (prev ? data.find(l => l.id === prev.id) || null : null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refrescar])

  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      if (filtroOrigen !== 'todos' && lead.origen !== filtroOrigen) return false
      if (search) {
        const q = search.toLowerCase()
        const principal = lead.contactos?.find(c => c.es_principal) || lead.contactos?.[0]
        const contactName = principal ? `${principal.nombre} ${principal.apellido || ''}`.toLowerCase() : ''
        return (
          lead.codigo.toLowerCase().includes(q) ||
          (lead.titulo?.toLowerCase().includes(q) ?? false) ||
          (lead.empresa?.toLowerCase().includes(q) ?? false) ||
          contactName.includes(q)
        )
      }
      return true
    })
  }, [leads, filtroOrigen, search])

  async function handleCreateLead() {
    if (!form.contacto_nombre.trim()) return
    setSaving(true)
    try {
      const tel = form.contacto_telefono.trim()
        ? `${form.codigo_telefono}${form.contacto_telefono.trim()}`
        : null
      setErrorAccion(null)
      await api.post('/api/leads', {
        contacto_nombre: form.contacto_nombre.trim(),
        contacto_apellido: form.contacto_apellido.trim() || null,
        contacto_email: form.contacto_email.trim() || null,
        contacto_telefono: tel,
        empresa: form.empresa.trim() || null,
        es_agencia: form.es_agencia,
        origen: form.origen,
      })
      setForm({ contacto_nombre: '', contacto_apellido: '', contacto_email: '', codigo_telefono: '+51', contacto_telefono: '', empresa: '', es_agencia: false, origen: 'web' })
      setShowNewLead(false)
      silentRefresh()
    } catch (err: any) {
      console.error('Error creating lead:', err)
      setErrorAccion(err?.message || 'No se pudo crear la oportunidad')
    }
    finally { setSaving(false) }
  }

  async function handleStageChange(leadId: number, newStage: EtapaPipeline, subestado?: string | null) {
    /*
      Arrastrar a Cerrado pregunta si se ganó o se perdió, igual que el botón
      de la ficha. La tarjeta NO se mueve todavía: si se mueve antes de la
      respuesta y la persona cancela, el tablero queda mostrando algo que no
      se guardó.
    */
    if (newStage === 'cerrado') {
      const lead = leads.find(l => l.id === leadId)
      if (lead) { setCerrando(lead); setCerrandoPaso(subestado); return }
    }
    const paso = subestado === undefined ? null : subestado
    actualizar(prev => prev.map(l => l.id === leadId ? { ...l, etapa: newStage, subestado: paso } : l))
    setSelectedLead(prev => prev && prev.id === leadId ? { ...prev, etapa: newStage } : prev)
    try {
      setErrorAccion(null)
      // Etapa y paso van juntos: son el mismo movimiento del tablero.
      await api.put(`/api/leads/${leadId}`, { etapa: newStage, subestado: paso })
      silentRefresh()
    } catch (err: any) {
      // La tarjeta vuelve sola a su columna por el refresco; sin el aviso,
      // parecía que el tablero se movía solo.
      setErrorAccion(err?.message || 'No se pudo mover la oportunidad de etapa')
      silentRefresh()
    }
  }

  function toggleSelect(id: number) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  async function handleBulkDelete() {
    if (selectedIds.size === 0) return
    setDeleting(true)
    try {
      setErrorAccion(null)
      await api.post('/api/leads/bulk-delete', { ids: [...selectedIds] })
      setSelectedIds(new Set())
      setShowDeleteConfirm(false)
      silentRefresh()
    } catch (err: any) {
      console.error('Bulk delete error:', err)
      // Se cierra igual el diálogo: el aviso va al tablero, donde se lee.
      setShowDeleteConfirm(false)
      setErrorAccion(err?.message || 'No se pudieron eliminar las oportunidades')
    }
    finally { setDeleting(false) }
  }

  async function handleMerge() {
    if (!mergeSurvivorId) return
    const absorbeIds = [...selectedIds].filter(id => id !== mergeSurvivorId)
    if (absorbeIds.length === 0) return
    try {
      await api.post('/api/leads/merge', {
        lead_sobrevive_id: mergeSurvivorId,
        lead_absorbe_ids: absorbeIds,
      })
      setErrorAccion(null)
      setSelectedIds(new Set())
      setShowMergeModal(false)
      setMergeSurvivorId(null)
      silentRefresh()
    } catch (err: any) {
      console.error('Merge error:', err)
      setShowMergeModal(false)
      setErrorAccion(err?.message || 'No se pudieron fusionar las oportunidades')
    }
  }

  function openMergeModal() {
    if (selectedIds.size < 2) return alert('Selecciona al menos 2 leads para fusionar')
    setMergeSurvivorId([...selectedIds][0])
    setShowMergeModal(true)
  }

  const selectedLeadsList = leads.filter(l => selectedIds.has(l.id))

  return (
    <div className="page-enter" id="page-pipeline">
      {selectedLead ? (
        <LeadDetailView lead={selectedLead} onClose={() => setSelectedLead(null)} onRefresh={silentRefresh} />
      ) : (
        <>
          <PageHeader
            breadcrumbs={[{ label: 'CRM' }, { label: 'Pipeline' }]}
            meta={`${filteredLeads.length} oportunidades`}
            actions={
              <button className="btn btn-primary" onClick={() => setShowNewLead(true)}>
                <IconPlus size={16} /> Nuevo Lead
              </button>
            }
          />


          {errorAccion && (
            <div className="crm-aviso-error" role="alert">
              <IconAlertCircle size={14} />
              <span>{errorAccion}</span>
              <button type="button" className="btn-icon-mini" onClick={() => setErrorAccion(null)} aria-label="Cerrar aviso">
                <IconX size={13} />
              </button>
            </div>
          )}

          {/* Bulk Action Bar */}
          {selectedIds.size > 0 && (
            <div className="bulk-action-bar">
              <span className="bulk-action-bar__count">{selectedIds.size} seleccionado(s)</span>
              <div className="bulk-action-bar__actions">
                <button className="btn btn-secondary btn-sm" onClick={() => setSelectedIds(new Set())}>
                  Deseleccionar
                </button>
                {!enPartner && (
                  <button className="btn btn-secondary btn-sm" onClick={openMergeModal} disabled={selectedIds.size < 2}>
                    <IconCopy size={14} /> Fusionar
                  </button>
                )}
                <button className="btn btn-danger btn-sm" onClick={() => setShowDeleteConfirm(true)}>
                  <IconTrash size={14} /> Eliminar
                </button>
              </div>
            </div>
          )}

          <div className="crm-filter-bar">
            <input className="form-input" placeholder="Buscar por código, nombre, empresa..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 320 }} />
            <div className="tags-filter-bar" style={{ marginBottom: 0 }}>
              <span className="tags-filter-label">Origen:</span>
              {ORIGIN_FILTERS.map(f => (
                <button key={f.key} className={`tag-chip${filtroOrigen === f.key ? ' active' : ''}`} onClick={() => setFiltroOrigen(f.key)}>{f.label}</button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="empty-state"><p>Cargando pipeline...</p></div>
          ) : filteredLeads.length === 0 && !search && filtroOrigen === 'todos' ? (
            <EmptyState icon={<IconTarget size={48} />} title="Sin leads en el pipeline" description="Crea tu primer lead para comenzar" actionLabel="+ Nuevo Lead" onAction={() => setShowNewLead(true)} />
          ) : (
            <PipelineBoard leads={filteredLeads} subestados={subestados} onStageChange={handleStageChange} onLeadClick={setSelectedLead} selectedIds={selectedIds} onToggleSelect={toggleSelect} />
          )}
        </>
      )}

      {/* Cierre: ganado o perdido */}
      {cerrando && (
        <CierreLeadModal
          lead={cerrando}
          subestado={cerrandoPaso}
          onCancelar={() => { setCerrando(null); setCerrandoPaso(undefined) }}
          onCerrado={res => {
            setCerrando(null)
            setCerrandoPaso(undefined)
            showToast(mensajeDeCierre(res), 'success')
            for (const aviso of res.avisos) showToast(aviso, 'warning')
            silentRefresh()
          }}
        />
      )}

      {/* New Lead Modal */}
      {showNewLead && (
        <div className="modal-overlay" onClick={() => setShowNewLead(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nuevo Lead</h3>
              <button className="modal-close" onClick={() => setShowNewLead(false)} type="button">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="modal-body">
              <p className="form-hint" style={{ marginBottom: 12, opacity: 0.7, fontSize: '0.82rem' }}>El código de oportunidad se genera automáticamente.</p>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Nombre del contacto *</label>
                  <input className="form-input" value={form.contacto_nombre} onChange={e => setForm(p => ({ ...p, contacto_nombre: e.target.value }))} placeholder="Juan" />
                </div>
                <div className="form-group">
                  <label className="form-label">Apellido</label>
                  <input className="form-input" value={form.contacto_apellido} onChange={e => setForm(p => ({ ...p, contacto_apellido: e.target.value }))} placeholder="Pérez" />
                </div>
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" value={form.contacto_email} onChange={e => setForm(p => ({ ...p, contacto_email: e.target.value }))} placeholder="juan@empresa.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <div className="lead-phone-input">
                    <select className="form-input lead-phone-code" value={form.codigo_telefono} onChange={e => setForm(p => ({ ...p, codigo_telefono: e.target.value }))}>
                      {COUNTRY_CODES.map(cc => (<option key={cc.code} value={cc.code}>{cc.label}</option>))}
                    </select>
                    <input className="form-input lead-phone-number" value={form.contacto_telefono} onChange={e => setForm(p => ({ ...p, contacto_telefono: e.target.value }))} placeholder="999 888 777" />
                  </div>
                </div>
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Empresa</label>
                  <input className="form-input" value={form.empresa} onChange={e => setForm(p => ({ ...p, empresa: e.target.value }))} placeholder="Nombre de la empresa" />
                </div>
                <div className="form-group">
                  <label className="form-label">Origen</label>
                  <select className="form-input" value={form.origen} onChange={e => setForm(p => ({ ...p, origen: e.target.value as OrigenLead }))}>
                    <option value="meta_ads">Meta Ads</option>
                    <option value="web">Web</option>
                    <option value="referido">Referido</option>
                    <option value="workana">Workana</option>
                    <option value="chatbot">Chatbot</option>
                    <option value="booking">Booking</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="radio_tv">Radio/TV</option>
                    <option value="campana_email">Campaña Email</option>
                    <option value="outbound">Outbound</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
              </div>
              {!enPartner && (
                <div className="form-group">
                  <label className="lead-agency-toggle">
                    <input type="checkbox" checked={form.es_agencia} onChange={e => setForm(p => ({ ...p, es_agencia: e.target.checked }))} />
                    <span className="lead-agency-toggle__label">🏢 Es una agencia (busca tercerizar servicios)</span>
                  </label>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowNewLead(false)} type="button">Cancelar</button>
              <button className="btn btn-primary" onClick={handleCreateLead} disabled={saving || !form.contacto_nombre.trim()} type="button">
                {saving ? 'Guardando...' : 'Crear Lead'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge Modal */}
      {showMergeModal && (
        <div className="modal-overlay" onClick={() => setShowMergeModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><IconCopy size={16} /> Fusionar Leads</h3>
              <button className="modal-close" onClick={() => setShowMergeModal(false)} type="button">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: '0.88rem' }}>
                Selecciona el lead que permanecerá. Los contactos de los demás se moverán a este lead.
              </p>
              <div className="merge-lead-list">
                {selectedLeadsList.map(lead => {
                  const principal = lead.contactos?.find(c => c.es_principal) || lead.contactos?.[0]
                  const name = principal ? `${principal.nombre} ${principal.apellido || ''}` : 'Sin contacto'
                  return (
                    <label key={lead.id} className={`merge-lead-option${mergeSurvivorId === lead.id ? ' active' : ''}`}>
                      <input type="radio" name="survivor" checked={mergeSurvivorId === lead.id} onChange={() => setMergeSurvivorId(lead.id)} />
                      <div className="merge-lead-option__info">
                        <span className="merge-lead-option__code">{lead.codigo}</span>
                        <span className="merge-lead-option__name">{lead.titulo || name}</span>
                        {lead.empresa && <span className="merge-lead-option__empresa">{lead.empresa}</span>}
                      </div>
                      {mergeSurvivorId === lead.id && <span className="merge-lead-option__badge">Permanece</span>}
                    </label>
                  )
                })}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowMergeModal(false)} type="button">Cancelar</button>
              <button className="btn btn-primary" onClick={handleMerge} disabled={!mergeSurvivorId} type="button">Fusionar</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3><IconTrash size={18} /> Eliminar Leads</h3>
              <button className="modal-close" onClick={() => setShowDeleteConfirm(false)} type="button">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', lineHeight: 1.5 }}>
                ¿Estás seguro de eliminar <strong style={{ color: 'var(--text-primary)' }}>{selectedIds.size} lead(s)</strong>? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)} type="button">Cancelar</button>
              <button className="btn btn-danger" onClick={handleBulkDelete} disabled={deleting} type="button">
                {deleting ? 'Eliminando...' : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
